import { createAsyncThunk } from "@reduxjs/toolkit";
import { getDataAPI, postDataAPI, putDataAPI } from "../../utils/FetchData";
import { setErrorAlert } from "../alert/alertSlice";
import socketService from "../../services/socket";
import { addMessage } from "./chatSlice";
import axios from "axios";
import { USER_AU_TOKEN } from "../../common/key";
import { BASE_URL } from "../../utils/FetchData";

// Send a message - Optimized to use socket directly when possible
export const send_message_thunk = createAsyncThunk(
  "user/chat/send",
  async (formData, thunkAPI) => {
    try {
      // Generate a temporary ID for this message
      const tempId = `temp-${Date.now()}`;
      
      // Create a message object to be used in both socket and API
      const messageData = {
        _id: tempId, // will be replaced by server's ID later
        chatRoomId: formData.chatRoomId,
        sender: formData.sender,
        senderName: formData.senderName,
        message: formData.message,
        createdAt: new Date().toISOString(),
        pending: true
      };

      
      // First add the message to Redux state immediately for responsiveness
      thunkAPI.dispatch(addMessage(messageData));
      
      // Try to send via socket first for real-time delivery
      const socketActive = socketService.sendMessage({
        roomId: formData.chatRoomId,
        senderId: formData.sender,
        senderName: formData.senderName,
        message: formData.message
      });
      
      // Regardless of socket status, also send to API for persistence
      const response = await postDataAPI("user/chat/send", formData);
      
      // Mark the message as no longer pending and use the server's ID
      return response.data;
    } catch (error) {
      console.error("Error sending message:", error);
      thunkAPI.dispatch(
        setErrorAlert(error?.response?.data?.message || "Failed to send message")
      );
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Failed to send message"
      );
    }
  }
);

// Get initial messages by chat room (latest messages)
export const get_initial_messages_thunk = createAsyncThunk(
  "user/chat/getInitial",
  async ({ roomId, userId, limit = 20 }, thunkAPI) => {
    try {
      // Clear userId parameter from URL as it's already in the header
      const response = await getDataAPI(`user/chat/room/${roomId}?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching messages:", error);
      thunkAPI.dispatch(
        setErrorAlert(error?.response?.data?.message || "Failed to fetch messages")
      );
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Failed to fetch messages"
      );
    }
  }
);

// Get older messages (messages before a certain ID)
export const get_older_messages_thunk = createAsyncThunk(
  "user/chat/getOlder",
  async ({ roomId, userId, beforeId, limit = 20 }, thunkAPI) => {
    try {
      // Clear userId parameter from URL as it's already in the header
      const response = await getDataAPI(`user/chat/room/${roomId}?before=${beforeId}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching older messages:", error);
      thunkAPI.dispatch(
        setErrorAlert(error?.response?.data?.message || "Failed to fetch older messages")
      );
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Failed to fetch older messages"
      );
    }
  }
);

// Get newer messages (messages after a certain ID)
export const get_newer_messages_thunk = createAsyncThunk(
  "user/chat/getNewer",
  async ({ roomId, userId, afterId, limit = 20 }, thunkAPI) => {
    try {
      // Clear userId parameter from URL as it's already in the header
      const response = await getDataAPI(`user/chat/room/${roomId}?after=${afterId}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching newer messages:", error);
      thunkAPI.dispatch(
        setErrorAlert(error?.response?.data?.message || "Failed to fetch newer messages")
      );
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Failed to fetch newer messages"
      );
    }
  }
);

// Mark messages as read
export const mark_messages_as_read_thunk = createAsyncThunk(
  "user/chat/markAsRead",
  async ({ roomId, userId }, thunkAPI) => {
    try {
      const response = await postDataAPI("user/chat/markAsRead", { roomId, userId });
      return { ...response.data, roomId };
    } catch (error) {
      console.error("Error marking messages as read:", error);
      thunkAPI.dispatch(
        setErrorAlert(error?.response?.data?.message || "Failed to mark messages as read")
      );
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Failed to mark messages as read"
      );
    }
  }
);

// Get unread message count
export const get_unread_message_count_thunk = createAsyncThunk(
  "user/chat/unreadCount",
  async (userId, thunkAPI) => {
    try {
      const response = await getDataAPI(`user/chat/unread`);
      return response.data;
    } catch (error) {
      console.error("Error fetching unread counts:", error);
      thunkAPI.dispatch(
        setErrorAlert(error?.response?.data?.message || "Failed to fetch unread counts")
      );
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Failed to fetch unread counts"
      );
    }
  }
);

// Upload an image message
export const upload_image_message_thunk = createAsyncThunk(
  "user/chat/uploadImage",
  async (formData, thunkAPI) => {
    try {
      // Generate a temporary ID for this message
      const tempId = `temp-${Date.now()}`;
      
      // Create a message object to show loading state
      const tempMessageData = {
        _id: tempId,
        chatRoomId: formData.get('chatRoomId'),
        sender: formData.get('sender'),
        senderName: formData.get('senderName'),
        message: formData.get('message') || 'Image',
        messageType: 'image',
        createdAt: new Date().toISOString(),
        pending: true,
        uploading: true
      };
      
      // Add the message to Redux state immediately to show loading state
      thunkAPI.dispatch(addMessage(tempMessageData));
      
      // Get token from localStorage
      const token = localStorage.getItem(USER_AU_TOKEN);
      
      // Upload the image via API (can't use socket for files)
      const response = await axios.post(`${BASE_URL}/user/chat/upload-image`, formData, {
        headers: { 
          Authorization: token,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Once uploaded successfully, emit via socket to notify other users
      if (response.data.success) {
        // Use socket event to notify others about the new image message
        socketService.sendMessage({
          roomId: formData.get('chatRoomId'),
          senderId: formData.get('sender'),
          senderName: formData.get('senderName'),
          message: response.data.data
        });
      }
      
      return response.data;
    } catch (error) {
      console.error("Error uploading image message:", error);
      thunkAPI.dispatch(
        setErrorAlert(error?.response?.data?.message || "Failed to upload image")
      );
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Failed to upload image"
      );
    }
  }
);

// Upload an audio message
export const upload_audio_message_thunk = createAsyncThunk(
  "user/chat/uploadAudio",
  async (formData, thunkAPI) => {
    try {
      // Generate a temporary ID for this message
      const tempId = `temp-${Date.now()}`;
      
      // Create a message object to show loading state
      const tempMessageData = {
        _id: tempId,
        chatRoomId: formData.get('chatRoomId'),
        sender: formData.get('sender'),
        senderName: formData.get('senderName'),
        message: formData.get('message') || 'Audio message',
        messageType: 'audio',
        duration: formData.get('duration') || null,
        createdAt: new Date().toISOString(),
        pending: true,
        uploading: true
      };
      
      // Add the message to Redux state immediately to show loading state
      thunkAPI.dispatch(addMessage(tempMessageData));
      
      // Get token from localStorage
      const token = localStorage.getItem(USER_AU_TOKEN);
      
      // Upload the audio via API (can't use socket for files)
      const response = await axios.post(`${BASE_URL}/user/chat/upload-audio`, formData, {
        headers: { 
          Authorization: token,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Once uploaded successfully, emit via socket to notify other users
      if (response.data.success) {
        // Use socket event to notify others about the new audio message
        socketService.sendMessage({
          roomId: formData.get('chatRoomId'),
          senderId: formData.get('sender'),
          senderName: formData.get('senderName'),
          message: response.data.data
        });
      }
      
      return response.data;
    } catch (error) {
      console.error("Error uploading audio message:", error);
      thunkAPI.dispatch(
        setErrorAlert(error?.response?.data?.message || "Failed to upload audio message")
      );
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Failed to upload audio message"
      );
    }
  }
);

// Update a message
export const update_message_thunk = createAsyncThunk(
  "user/chat/update",
  async ({ messageId, message, roomId, sender, preventScroll = false }, thunkAPI) => {
    try {
      // Store the original message before modification (for rollback in case of error)
      const currentState = thunkAPI.getState();
      const originalMessage = currentState.chat.messages.find(msg => msg._id === messageId);
      
      if (!originalMessage) {
        throw new Error("Message not found for editing");
      }
      
      // Optimistically update the message in the UI immediately
      thunkAPI.dispatch({
        type: "chat/updateMessage",
        payload: {
          messageId,
          message,
          isEditing: false,
          isEdited: true,
          editedAt: new Date().toISOString(),
          preventScroll: preventScroll
        }
      });
      
      // Try to send via socket for real-time update
      const socketSuccess = socketService.editMessage({
        roomId,
        messageId,
        message: {
          message,
          sender,
          editedAt: new Date().toISOString()
        },
        preventScroll: preventScroll
      });
      
      if (!socketSuccess) {
        console.warn('Socket update failed, but continuing with API update');
      }
      
      // Send to API for persistence
      const response = await putDataAPI(`user/chat/${messageId}`, { 
        message,
        preventScroll
      });
      
      return response.data;
    } catch (error) {
      console.error("Error editing message:", error);
      
      // Get the original message to revert to
      const currentState = thunkAPI.getState();
      const originalMessage = currentState.chat.messages.find(msg => msg._id === messageId);
      
      // Revert the optimistic update in case of error
      if (originalMessage) {
        thunkAPI.dispatch({
          type: "chat/updateMessage",
          payload: {
            messageId,
            message: originalMessage.message, // Revert to original content
            isEditing: false,
            isEdited: originalMessage.isEdited, // Keep original edited state
            editedAt: originalMessage.editedAt, // Keep original edit timestamp
            error: error?.response?.data?.message || "Failed to edit message"
          }
        });
      }
      
      thunkAPI.dispatch(
        setErrorAlert(error?.response?.data?.message || "Failed to edit message")
      );
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Failed to edit message"
      );
    }
  }
); 